import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Logo, LoadingSpinner } from '../common';
import { useAuth } from '../../context/AuthContext';
import { investorTypes, currencies, moodysRatings, investmentTerms } from '../../data/mockData';
import Footer from '../common/Footer';

const InvestorRegistrationPage = () => {
  const { register, loading } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    investorType: '',
    investorTypeOther: '',
    jobRole: '',
    ticketMin: '',
    ticketMax: '',
    preferredCurrency: '',
    targetYieldMin: '',
    targetYieldMax: '',
    moodysRatingMin: '',
    moodysRatingMax: '',
    preferredTerm: '',
  });
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    const result = await register({ ...formData, type: 'investor' });
    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error || 'Registration failed');
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-white text-black font-sans flex flex-col justify-between antialiased">
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white rounded-none p-8 max-w-md w-full text-center border border-zinc-300">
            <div className="w-12 h-12 border border-zinc-300 bg-zinc-50 rounded-none flex items-center justify-center mx-auto mb-6">
              <svg className="w-6 h-6 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Registration Submitted</span>
            <h2 className="font-sans text-2xl font-bold text-black mb-3">Application Under Review</h2>
            <p className="font-sans text-xs text-zinc-600 leading-relaxed mb-8">
              Your investor account application is pending verification. You will receive an email once your account opening process begins.
            </p>
            <Link to="/login" className="py-3 px-6 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all inline-block w-full">
              Return to Platform Login
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black font-sans antialiased flex flex-col justify-between">
      <div>
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex items-center justify-between h-20">
              <Logo />
              <div className="hidden lg:flex items-center gap-6">
                <Link to="/pricing" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Pricing</Link>
                <Link to="/contact" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Contact</Link>
                <Link to="/login" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-800 hover:text-black font-medium">Platform Login</Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Header Banner */}
        <div className="pt-32 pb-16 bg-[#4D4D4D] text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
            <Link to="/register" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-zinc-300 hover:text-white mb-6">
              <ArrowLeft className="w-4 h-4" /> Back to Option Selection
            </Link>
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-300 block mb-2">Investor Onboarding</span>
            <h1 className="font-sans text-3xl md:text-4xl font-bold text-white mb-4">Investor Registration</h1>
            <p className="font-sans text-base text-zinc-300 max-w-xl mx-auto">
              Open a T-bill investment account. Pre-KYC approval, bank-facilitated depository account setup.
            </p>
          </div>
        </div>

        {/* Form Container */}
        <div className="max-w-2xl mx-auto px-6 py-16">
          <div className="bg-white border border-zinc-300 p-8 md:p-10 rounded-none">
            {error && (
              <div className="mb-8 p-4 bg-zinc-50 border border-zinc-300 rounded-none text-black font-mono text-xs uppercase tracking-wider">
                ⚠️ {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Account Credentials */}
              <section>
                <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Step 01</span>
                <h3 className="font-sans text-lg font-bold text-black mb-4">Account Credentials</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block font-mono text-xs uppercase tracking-wider text-zinc-700 mb-2">Work Email Address</label>
                    <input 
                      type="email" 
                      name="email" 
                      value={formData.email} 
                      onChange={handleChange} 
                      className="input-field" 
                      placeholder="investor@fund.com"
                      required 
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-xs uppercase tracking-wider text-zinc-700 mb-2">Password</label>
                    <input 
                      type="password" 
                      name="password" 
                      value={formData.password} 
                      onChange={handleChange} 
                      className="input-field" 
                      placeholder="Min. 8 characters"
                      required 
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-xs uppercase tracking-wider text-zinc-700 mb-2">Confirm Password</label>
                    <input 
                      type="password" 
                      name="confirmPassword" 
                      value={formData.confirmPassword} 
                      onChange={handleChange} 
                      className="input-field" 
                      required 
                    />
                  </div>
                </div>
              </section>

              <hr className="border-zinc-200" />

              {/* Investor Profile */}
              <section>
                <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Step 02</span>
                <h3 className="font-sans text-lg font-bold text-black mb-4">Investor Profile</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block font-mono text-xs uppercase tracking-wider text-zinc-700 mb-2">Investor Type</label>
                    <select 
                      name="investorType" 
                      value={formData.investorType} 
                      onChange={handleChange} 
                      className="select-field" 
                      required
                    >
                      <option value="">Select investor type</option>
                      {investorTypes.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block font-mono text-xs uppercase tracking-wider text-zinc-700 mb-2">Job Role</label>
                    <input 
                      type="text" 
                      name="jobRole" 
                      value={formData.jobRole} 
                      onChange={handleChange} 
                      className="input-field" 
                      placeholder="e.g., Managing Partner, CIO, Portfolio Manager"
                    />
                  </div>
                </div>
              </section>

              <hr className="border-zinc-200" />

              {/* Mandate Preferences */}
              <section>
                <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Step 03</span>
                <h3 className="font-sans text-lg font-bold text-black mb-4">Investment Mandate Preferences</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block font-mono text-xs uppercase tracking-wider text-zinc-700 mb-2">Min Ticket ($)</label>
                      <input 
                        type="number" 
                        name="ticketMin" 
                        value={formData.ticketMin} 
                        onChange={handleChange} 
                        className="input-field" 
                        placeholder="50,000"
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-xs uppercase tracking-wider text-zinc-700 mb-2">Max Ticket ($)</label>
                      <input 
                        type="number" 
                        name="ticketMax" 
                        value={formData.ticketMax} 
                        onChange={handleChange} 
                        className="input-field" 
                        placeholder="5,000,000"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block font-mono text-xs uppercase tracking-wider text-zinc-700 mb-2">Preferred Currency</label>
                    <select 
                      name="preferredCurrency" 
                      value={formData.preferredCurrency} 
                      onChange={handleChange} 
                      className="select-field"
                    >
                      <option value="">Select currency</option>
                      {currencies.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </section>

              <button 
                type="submit" 
                disabled={loading} 
                className="w-full py-4 px-6 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all flex items-center justify-center gap-2"
              >
                {loading ? <LoadingSpinner size="small" /> : 'Submit Investor Application'}
              </button>

              <p className="text-center font-mono text-xs text-zinc-500">
                Already registered? <Link to="/login" className="text-black font-bold underline uppercase tracking-wider">Sign In</Link>
              </p>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default InvestorRegistrationPage;
